import type { FunctionComponentWithRef } from "react";
import { useCallback } from "react";
import { graphql, useFragment } from "react-relay";

import { useTrackImpressionEventByIntersection } from "shared/hooks";
import { notEmpty } from "shared/lib/utils";

import { ContentItemHomeSecondaryV2 } from "scmp-app/components/content/content-item-render/variants/home-secondary-v2";
import { getContentSponsor } from "scmp-app/components/content/content-sponsor-tag/helpers";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { RecirculationWidgetImpressionEvent } from "scmp-app/components/tracking/google-analytics-4/types";
import type { topStoriesItemV2Content$key } from "scmp-app/queries/__generated__/topStoriesItemV2Content.graphql";

import { Container } from "./styles";

export type Props = {
  className?: string;
  isShowImage?: boolean;
  onClick?: (entityId?: string, urlAlias?: string, isSponsorArticle?: boolean) => void;
  reference: topStoriesItemV2Content$key;
  withCarousel?: boolean;
  withExclusiveHeaderFlag?: boolean;
  withLiveTag?: boolean;
  withSection?: boolean;
  withSeriesShortLabel?: boolean;
  withSummary?: boolean;
  withVideoClickToPlay?: boolean;
};

export const HomeV2TopStoriesItem: FunctionComponentWithRef<Props, HTMLDivElement> = ({
  className,
  isShowImage = false,
  onClick,
  ref,
  reference,
  withCarousel = false,
  withExclusiveHeaderFlag = false,
  withLiveTag,
  withSection = true,
  withSeriesShortLabel = false,
  withSummary = false,
  withVideoClickToPlay = false,
}) => {
  const content = useFragment(
    graphql`
      fragment topStoriesItemV2Content on Content {
        ...helpersUseContentSponsorContent
        ...homeSecondaryV2ContentItemContent
      }
    `,
    reference,
  );

  const contentSponsor = getContentSponsor(content);
  const isSponsorArticle = notEmpty(contentSponsor?.name);

  const sponsorArticleImpressionGA4Event = useCallback<() => RecirculationWidgetImpressionEvent>(
    () => ({
      action: "imp",
      category: "recirculation",
      customized_parameters: {
        page_type: "homepage",
        widget_name: "sponsor_article",
      },
      subcategory: "widget",
    }),
    [],
  );

  const { captureTrackImpressionEventTargetElement: captureSponsorArticleImpressionEvent } =
    useTrackImpressionEventByIntersection({
      ga4TrackingHandler: sendGA4Tracking,
      getGa4Event: sponsorArticleImpressionGA4Event,
      options: { isSendGA4Tracking: true, shouldSendOnce: true },
    });

  return (
    <Container className={className} ref={ref}>
      <ContentItemHomeSecondaryV2
        onClick={(entityId, urlAlias) => onClick?.(entityId, urlAlias, isSponsorArticle)}
        ref={isSponsorArticle ? captureSponsorArticleImpressionEvent : null}
        reference={content}
        withCarousel={withCarousel}
        withComment={true}
        withExclusiveHeaderFlag={withExclusiveHeaderFlag}
        withImage={isShowImage}
        withLiveTag={withLiveTag ?? true}
        withSection={withSection}
        withSeriesShortLabel={withSeriesShortLabel}
        withSummary={withSummary}
        withVideoClickToPlay={withVideoClickToPlay}
      />
    </Container>
  );
};

HomeV2TopStoriesItem.displayName = "HomeV2TopStoriesItem";
